import * as Yup from 'yup';

import {
  ApiEndpoint,
  columnDirection,
  FormActionType,
  FormType,
  InputType,
  ServiceModalName,
  Translation,
  WafSchemaType,
} from '@enums';
import i18n from '@translations';
import type { EndpointType, FormConfig, FormConfigMapping, OptionItem } from '@types';

interface GetWAFFormConfigParams {
  endpoint?: EndpointType;
  options?: OptionItem<string | number>[];
  schema?: Yup.AnySchema;
}

export const getWAFFormConfig = ({
  endpoint = ApiEndpoint.DEFAULT,
  options = [],
  schema,
}: GetWAFFormConfigParams): FormConfig => {
  const optionWithPlaceholder = (
    list: OptionItem<string | number>[] = [],
    placeholder: string,
  ): OptionItem<string | number>[] => [{ value: '', label: placeholder }, ...list];

  const WAF_FORM: FormConfigMapping = {
    [ApiEndpoint.DEFAULT]: {
      formType: FormType.CdnWAF,
      endpoint: ApiEndpoint.DEFAULT,
      formActionType: FormActionType.Save,
      inputList: [
        {
          name: 'status',
          defaultValue: false,
          inputType: InputType.Switch,
          label: i18n.t(`${Translation.Configuration}:forms.waf.configuration.status`),
          description: i18n.t(`${Translation.Configuration}:tabs.waf.configuration.description.enabled`),
          direction: columnDirection.Row,
        },
        {
          name: 'log_sensitivity',
          inputType: InputType.Select,
          placeholder: i18n.t(`${Translation.Configuration}:forms.waf.configuration.selectYourLogSensitivity`),
          label: i18n.t(`${Translation.Configuration}:forms.waf.configuration.logSensitivity`),
          description: i18n.t(`${Translation.Configuration}:tabs.waf.configuration.description.logSensitivity`),
          direction: columnDirection.Row,
          options: options[WafSchemaType.Sensitivity],
          className: 'space-y-3',
          inactive: {
            field: 'status',
            condition: (value: boolean) => !value,
          },
        },
        {
          name: 'block_sensitivity',
          inputType: InputType.Select,
          placeholder: i18n.t(`${Translation.Configuration}:forms.waf.configuration.selectYourBlockSensitivity`),
          label: i18n.t(`${Translation.Configuration}:forms.waf.configuration.blockSensitivity`),
          description: i18n.t(`${Translation.Configuration}:tabs.waf.configuration.description.blockSensitivity`),
          direction: columnDirection.Row,
          options: options[WafSchemaType.Sensitivity],
          className: 'space-y-3',
          inactive: {
            field: 'status',
            condition: (value: boolean) => !value,
          },
        },
        {
          name: 'block_action',
          inputType: InputType.Select,
          placeholder: i18n.t(`${Translation.Configuration}:forms.waf.configuration.selectYourBlockAction`),
          label: i18n.t(`${Translation.Configuration}:forms.waf.configuration.blockAction`),
          description: i18n.t(`${Translation.Configuration}:tabs.waf.configuration.description.blockAction`),
          direction: columnDirection.Row,
          options: options[WafSchemaType.BlockAction],
          className: 'space-y-3',
          inactive: {
            field: 'status',
            condition: (value: boolean) => !value,
          },
        },
        {
          name: 'new_rule_status',
          inputType: InputType.Select,
          placeholder: i18n.t(`${Translation.Configuration}:forms.waf.configuration.selectYourNewRuleStatus`),
          label: i18n.t(`${Translation.Configuration}:forms.waf.configuration.newRuleStatus`),
          description: i18n.t(`${Translation.Configuration}:tabs.waf.configuration.description.newRuleStatus`),
          direction: columnDirection.Row,
          options: options[WafSchemaType.RuleStatus],
          className: 'space-y-3',
          inactive: {
            field: 'status',
            condition: (value: boolean) => !value,
          },
        },
      ],
    },
    [ApiEndpoint.RULE]: {
      formType: FormType.CdnWAF,
      endpoint: ApiEndpoint.RULE,
      formActionType: FormActionType.Filter,
      modalName: ServiceModalName.FormModal,
      label: {
        title: i18n.t(`${Translation.Configuration}:forms.waf.ruleSets.filterWafRuleSets`),
        description: i18n.t(`${Translation.Configuration}:forms.waf.ruleSets.applyFilterToRefineWafRules`),
        confirmButton: i18n.t(`${Translation.Configuration}:buttons.waf.applyFilters`),
        cancelButton: i18n.t(`${Translation.Configuration}:buttons.waf.resetAll`),
      },
      inputList: [
        {
          name: 'rule_name',
          defaultValue: '',
          inputType: InputType.Text,
          label: i18n.t(`${Translation.Configuration}:tables.rulesName`),
          placeholder: i18n.t(`${Translation.Configuration}:forms.waf.ruleSets.searchByRuleName`),
          direction: columnDirection.Row,
        },
        {
          name: 'group_id',
          inputType: InputType.Select,
          label: i18n.t(`${Translation.Configuration}:tables.groupName`),
          direction: columnDirection.Row,
          allowEmptyValue: true,
          options: optionWithPlaceholder(
            options[WafSchemaType.RuleGroupName],
            i18n.t(`${Translation.Configuration}:forms.waf.ruleSets.selectYourGroupName`),
          ),
        },
        {
          name: 'sensitivity',
          inputType: InputType.Select,
          label: i18n.t(`${Translation.Configuration}:tables.sensitivity`),
          direction: columnDirection.Row,
          allowEmptyValue: true,
          options: optionWithPlaceholder(
            options[WafSchemaType.Sensitivity],
            i18n.t(`${Translation.Configuration}:forms.waf.ruleSets.selectYourSensitivity`),
          ),
        },
        {
          name: 'rule_status',
          inputType: InputType.Select,
          label: i18n.t(`${Translation.Configuration}:tables.ruleStatus`),
          direction: columnDirection.Row,
          allowEmptyValue: true,
          options: optionWithPlaceholder(
            options[WafSchemaType.RuleStatus],
            i18n.t(`${Translation.Configuration}:forms.waf.ruleSets.selectYourRuleStatus`),
          ),
        },
      ],
    },
  };

  return (WAF_FORM[endpoint] ?? WAF_FORM[ApiEndpoint.DEFAULT]) as FormConfig;
};
